import { isDefined } from "@clipboard-health/util-ts";
import { API_TYPES, patch } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { useMutation, type UseMutationOptions, useQueryClient } from "@tanstack/react-query";
import { type z } from "zod";

import {
  type FacilityUserInternalNotesResponse,
  facilityUserInternalNotesResponseSchema,
  facilityUserInternalNotesSchema,
  updateFacilityUserInternalNotesRequestSchema,
} from "../types";
import { getFacilityUserInternalNotesUrl } from "./useGetFacilityUserInternalNotes";

export const UPDATE_FACILITY_USER_INTERNAL_NOTES_URL = `${environmentConfig.REACT_APP_API_MAIN_URL}/api/facility-user-internal-notes/:facilityUserId-:workplaceId`;

// FIXME: Automatically disabled during ESLint config update
const updateFacilityUserInternalNotesParamsSchema =
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  facilityUserInternalNotesSchema.shape.attributes;
type UpdateFacilityUserInternalNotesParams = z.infer<
  typeof updateFacilityUserInternalNotesParamsSchema
>;

export function useUpdateFacilityUserInternalNotes(
  options: UseMutationOptions<
    FacilityUserInternalNotesResponse,
    unknown,
    UpdateFacilityUserInternalNotesParams
  > = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params) => {
      const { facilityUserId, workplaceId, notes } = params;

      const response = await patch({
        url: UPDATE_FACILITY_USER_INTERNAL_NOTES_URL.replace(
          ":facilityUserId",
          facilityUserId
        ).replace(":workplaceId", workplaceId),
        data: {
          data: {
            id: `${facilityUserId}-${workplaceId}`,
            type: API_TYPES.facilityUserInternalNotes,
            attributes: { notes },
          },
        },
        requestSchema: updateFacilityUserInternalNotesRequestSchema,
        responseSchema: facilityUserInternalNotesResponseSchema,
      });

      return response.data;
    },
    ...options,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.UPDATE_FACILITY_USER_INTERNAL_NOTES_FAILURE,
      ...options.meta,
    },
    onSuccess: (response, variables, context) => {
      // eslint-disable-next-line no-console
      console.log(
        getFacilityUserInternalNotesUrl({
          workplaceId: response.data.attributes.workplaceId,
          facilityUserId: response.data.attributes.facilityUserId,
        })
      );
      queryClient.setQueryData(
        [
          getFacilityUserInternalNotesUrl({
            workplaceId: response.data.attributes.workplaceId,
            facilityUserId: response.data.attributes.facilityUserId,
          }),
          undefined,
        ],
        { data: response.data } as FacilityUserInternalNotesResponse
      );
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      isDefined(options.onSuccess) && options.onSuccess(response, variables, context);
    },
  });
}
